import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TableSortLabel
} from '@mui/material';
import {
  TrendingUp,
  LocalOffer,
  ShoppingCart,
  AttachMoney,
  EmojiEvents,
  Assessment,
  Visibility
} from '@mui/icons-material';
import OfferService from '../../services/offers';
import { ccyFormat } from '../../helpers/helper';
import OfferStatsModal from './OfferStatsModal';

const OffersDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('total_revenue');
  const [order, setOrder] = useState('desc');
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [selectedOfferName, setSelectedOfferName] = useState('');
  const [openStatsModal, setOpenStatsModal] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await OfferService.getAllStats();
      setStats(data);
    } catch (err) {
      setError('Error al cargar las estadísticas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (offer) => {
    setSelectedOfferId(offer.offer_id);
    setSelectedOfferName(offer.offer_name);
    setOpenStatsModal(true);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedOffers = stats?.offers ? [...stats.offers].sort((a, b) => {
    const aValue = a[orderBy] || 0;
    const bValue = b[orderBy] || 0;
    return order === 'asc' ? aValue - bValue : bValue - aValue;
  }) : [];

  const KPICard = ({ title, value, subtitle, icon: Icon, gradient, trend }) => (
    <Card
      elevation={3}
      sx={{
        height: '100%',
        background: gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500, textTransform: 'uppercase' }}>
            {title}
          </Typography>
          {Icon && (
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Icon sx={{ fontSize: 24 }} />
            </Box>
          )}
        </Box>
        <Typography variant="h4" fontWeight="bold" mb={0.5}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -20,
              right: -20,
              opacity: 0.1,
              fontSize: 120
            }}
          >
            <Icon sx={{ fontSize: 'inherit' }} />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const TopOfferCard = ({ title, offer, icon: Icon, color }) => (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '2px solid',
        borderColor: `${color}.light`,
        bgcolor: `${color}.50`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        {Icon && <Icon sx={{ color: `${color}.main`, fontSize: 28 }} />}
        <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h6" fontWeight="bold" color="text.primary" mb={0.5}>
        {offer?.offer_name || 'N/A'}
      </Typography>
      <Box display="flex" gap={2}>
        {offer?.total_uses !== undefined && (
          <Typography variant="body2" color="text.secondary">
            <strong>{offer.total_uses}</strong> usos
          </Typography>
        )}
        {offer?.total_revenue !== undefined && (
          <Typography variant="body2" color="text.secondary">
            <strong>${ccyFormat(offer.total_revenue)}</strong>
          </Typography>
        )}
        {offer?.total_discount !== undefined && (
          <Typography variant="body2" color="text.secondary">
            <strong>${ccyFormat(offer.total_discount)}</strong> desc.
          </Typography>
        )}
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) return null;

  return (
    <Box>
      {/* KPIs principales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Total de Ofertas"
            value={stats.total_offers || 0}
            subtitle={`${stats.active_offers || 0} activas • ${stats.expired_offers || 0} expiradas`}
            icon={LocalOffer}
            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            trend
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Ingresos Totales"
            value={`$${ccyFormat(stats.total_revenue || 0)}`}
            subtitle="Generados con ofertas"
            icon={AttachMoney}
            gradient="linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)"
            trend
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Descuentos Otorgados"
            value={`$${ccyFormat(stats.total_discount || 0)}`}
            subtitle={`${stats.total_uses || 0} usos totales`}
            icon={TrendingUp}
            gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
            trend
          />
        </Grid>
      </Grid>

      {/* Ofertas destacadas */}
      <Typography variant="h6" fontWeight="600" mb={2} display="flex" alignItems="center" gap={1}>
        <EmojiEvents color="warning" />
        Ofertas Destacadas
      </Typography>
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} md={4}>
          <TopOfferCard
            title="🥇 Más Usada"
            offer={stats.most_used_offer}
            icon={ShoppingCart}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TopOfferCard
            title="💰 Mayor Ingreso"
            offer={stats.highest_revenue}
            icon={AttachMoney}
            color="success"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TopOfferCard
            title="🎁 Mayor Descuento"
            offer={stats.highest_discount}
            icon={LocalOffer}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Tabla comparativa */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" fontWeight="600" display="flex" alignItems="center" gap={1}>
            <Assessment />
            Comparativa de Ofertas
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'offer_name'}
                    direction={orderBy === 'offer_name' ? order : 'asc'}
                    onClick={() => handleSort('offer_name')}
                  >
                    <strong>Oferta</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'total_uses'}
                    direction={orderBy === 'total_uses' ? order : 'asc'}
                    onClick={() => handleSort('total_uses')}
                  >
                    <strong>Usos</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'total_revenue'}
                    direction={orderBy === 'total_revenue' ? order : 'asc'}
                    onClick={() => handleSort('total_revenue')}
                  >
                    <strong>Ingresos</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'total_discount'}
                    direction={orderBy === 'total_discount' ? order : 'asc'}
                    onClick={() => handleSort('total_discount')}
                  >
                    <strong>Descuento</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'total_items_sold'}
                    direction={orderBy === 'total_items_sold' ? order : 'asc'}
                    onClick={() => handleSort('total_items_sold')}
                  >
                    <strong>Items</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedOffers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No hay ofertas con estadísticas disponibles
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedOffers.map((offer) => (
                  <TableRow
                    key={offer.offer_id}
                    sx={{
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.02)',
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => handleViewDetails(offer)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {offer.offer_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {offer.offer_type}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {offer.is_expired ? (
                        <Chip label="Expirada" size="small" color="error" />
                      ) : offer.active ? (
                        <Chip label="Activa" size="small" color="success" />
                      ) : (
                        <Chip label="Inactiva" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600">
                        {offer.total_uses || 0}
                      </Typography>
                      {offer.current_uses !== undefined && (
                        <Typography variant="caption" color="text.secondary">
                          ({offer.current_uses} actual)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600" color="success.main">
                        ${ccyFormat(offer.total_revenue || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600" color="error.main">
                        ${ccyFormat(offer.total_discount || 0)}
                      </Typography>
                      {offer.avg_discount_per_use !== undefined && (
                        <Typography variant="caption" color="text.secondary">
                          Prom: ${ccyFormat(offer.avg_discount_per_use)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {offer.total_items_sold || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalles">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(offer);
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal de estadísticas individuales */}
      <OfferStatsModal
        open={openStatsModal}
        onClose={() => setOpenStatsModal(false)}
        offerId={selectedOfferId}
        offerName={selectedOfferName}
      />
    </Box>
  );
};

export default OffersDashboard;
